import React from 'react'
import { Tag, X } from 'lucide-react'
import type { ProjectLabel } from '@bandos/shared'
import { DismissibleTooltip } from '../../../../common/DismissibleTooltip'
import { LabelSelector } from '../../../Menus/LabelSelector'
import { getContrastingTextColor } from '../../../../../utils/colorUtils'
import { ExternalLabelsContainer, ExternalAddLabelButton } from '../styles/BaseNodeStyles'
import { RemoveLabelButton, LabelChip, LabelChipText } from '../styles/LabelStyles'
import type { LabelContextType } from '../../../../../context/label/LabelContextDef'

interface NodeLabelsProps {
  labels: ProjectLabel[] | undefined
  nodeId: string
  mindmapId: string | undefined
  isReadOnly: boolean
  labelContext: LabelContextType
  onSelectLabel: (labelId: string) => Promise<void>
  onCreateLabel: (labelText: string, color: string) => Promise<void>
  onRemoveLabel: (labelId: string) => Promise<void>
  onDeleteLabel: (labelId: string) => Promise<void>
}

export const NodeLabels = React.memo(
  ({
    labels,
    isReadOnly,
    labelContext,
    onSelectLabel,
    onCreateLabel,
    onRemoveLabel,
    onDeleteLabel,
  }: NodeLabelsProps) => (
    <ExternalLabelsContainer>
      {labels && labels.length > 0 && (
        <>
          {labels.map((label) => (
            <LabelChip key={label.id} $color={label.color}>
              <LabelChipText>{label.label_text}</LabelChipText>
              {!isReadOnly && (
                <RemoveLabelButton
                  className="nodrag"
                  $bgColor={label.color}
                  onClick={(e) => {
                    e.stopPropagation()
                    void onRemoveLabel(label.id)
                  }}
                  title="Remove label"
                >
                  <X size={9} color={getContrastingTextColor(label.color)} strokeWidth={2.5} />
                </RemoveLabelButton>
              )}
            </LabelChip>
          ))}
        </>
      )}
      {!isReadOnly && (
        <>
          <LabelSelector
            labels={labelContext.labels}
            selectedLabelIds={labels?.map((l) => l.id) ?? []}
            onSelectLabel={onSelectLabel}
            onCreateLabel={onCreateLabel}
            onDeleteLabel={onDeleteLabel}
          >
            <DismissibleTooltip
              title={
                labels && labels.length > 0 ? 'Add more tags' : 'Organize idea chains with labels'
              }
              placement="right"
              mouseEnterDelay={0}
            >
              <ExternalAddLabelButton
                className="nodrag"
                onClick={(e: React.MouseEvent) => e.stopPropagation()}
              >
                <Tag size={11} />
                {(!labels || labels.length === 0) && <span>Add label</span>}
              </ExternalAddLabelButton>
            </DismissibleTooltip>
          </LabelSelector>
        </>
      )}
    </ExternalLabelsContainer>
  )
)
